import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '../common/Button';
import './JobDetail.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const VendorJobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasSubmittedBid, setHasSubmittedBid] = useState(false);
  const [myBid, setMyBid] = useState(null);
  const [canMessageOwner, setCanMessageOwner] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState('');

  const getToken = () => localStorage.getItem('marilink_token');
  const currentUser = JSON.parse(localStorage.getItem('marilink_user') || '{}');

  useEffect(() => {
    const fetchJobDetails = async () => {
      const token = getToken();
      if (!token) {
        setError('Authentication required. Please login again.');
        setLoading(false);
        setChecking(false);
        return;
      }

      try {
        const jobRes = await fetch(`${API_BASE}/rfqs/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const jobData = await jobRes.json();
        
        if (jobData.success) {
          setJob(jobData.rfq);
          
          // Fetch owner details
          if (jobData.rfq.ownerId) {
            const ownerRes = await fetch(`${API_BASE}/users/${jobData.rfq.ownerId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const ownerData = await ownerRes.json();
            if (ownerData.success) {
              setJob(prev => ({ ...prev, ownerName: ownerData.user?.name, ownerId: ownerData.user?._id }));
            }
          }
          
          // Check if vendor can message owner (if contract exists)
          try {
            const contractsRes = await fetch(`${API_BASE}/contracts`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const contractsData = await contractsRes.json();
            const hasContract = contractsData.contracts?.some(c => 
              c.rfqId === id && ['pending_payment', 'payment_uploaded', 'payment_verified', 'work_in_progress', 'work_completed', 'payment_released', 'cancelled'].includes(c.status)
            );
            
            if (hasContract) {
              setCanMessageOwner(true);
            } else {
              // Check if owner ever messaged
              const statusRes = await fetch(`${API_BASE}/messages/conversation-status/${jobData.rfq.ownerId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              const statusData = await statusRes.json();
              setCanMessageOwner(statusData.success && statusData.hasOwnerMessaged);
            }
          } catch (e) {
            console.warn('Error checking message permission:', e);
            setCanMessageOwner(false);
          }
          
          // Check if vendor already submitted a bid
          const bidsRes = await fetch(`${API_BASE}/bids`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const bidsData = await bidsRes.json();
          if (bidsData.success) {
            const myBid = bidsData.bids.find(b => b.rfqId === id && b.vendorId === currentUser._id);
            if (myBid) {
              setHasSubmittedBid(true);
              setMyBid(myBid);
            }
          }
        } else {
          setError(jobData.message || 'Job not found');
        }
      } catch (error) {
        console.error('Error fetching job:', error);
        setError('Failed to load job details. Please try again.');
      } finally {
        setLoading(false);
        setChecking(false);
      }
    };
    fetchJobDetails();
  }, [id, currentUser._id]);

  const handleContactOwner = () => {
    if (job?.ownerId) {
      navigate(`/messages/${job.ownerId}`, { 
        state: { userName: job.ownerName, jobTitle: job.serviceType }
      });
    } else {
      alert('Owner information not available.');
    }
  };

  const getBudgetDisplay = () => {
    if (!job) return 'N/A';
    if (job.budgetType === 'quotation' || (!job.budget && !job.budgetMin)) {
      return 'Request for Quotation';
    }
    if (job.budgetType === 'hourly') {
      const rate = job.budget || job.budgetMin || 0;
      return `$${rate.toLocaleString()}/hr`;
    }
    const amount = job.budget || job.budgetMin || 0;
    return `$${amount.toLocaleString()}`;
  };

  if (loading || checking) return <div className="loading-spinner">Loading job details...</div>;
  if (error) return (
    <div className="error-message" style={{ textAlign: 'center', padding: '40px' }}>
      <p>{error}</p>
      <Button variant="secondary" onClick={() => navigate(-1)} style={{ marginTop: '16px' }}>Go Back</Button>
    </div>
  );
  if (!job) return <div className="error-message">Job not found</div>;

  const isJobOpen = job.status === 'open';
  const isJobExpired = job.deadline && new Date(job.deadline) < new Date();

  return (
    <div className="vendor-job-detail">
      <button className="back-btn" onClick={() => navigate(-1)}>
        <i className="fas fa-arrow-left"></i> Back to Available Jobs
      </button>

      <div className="job-card">
        <div className="job-header">
          <h1>{job.vesselName}</h1>
          <div className="job-meta">
            <span className="job-id">#{job._id?.slice(-8)}</span>
            <span className={`job-status ${job.status}`}>
              {job.status === 'open' ? (isJobExpired ? 'Expired' : 'Open') : job.status}
            </span>
          </div>
        </div>

        <div className="job-body">
          <div className="info-grid">
            <div><span className="label">Current Port</span><span className="value"><i className="fas fa-map-marker-alt"></i> {job.port || 'Not specified'}</span></div>
            {job.nextPort && <div><span className="label">Next Port</span><span className="value"><i className="fas fa-arrow-right"></i> {job.nextPort}</span></div>}
            <div><span className="label">Service Type</span><span className="value">{job.serviceType}</span></div>
            <div><span className="label">Budget Type</span><span className="value">{job.budgetType === 'quotation' ? 'Request Quotation' : job.budgetType === 'hourly' ? 'Per Hour' : 'Fixed Price'}</span></div>
            <div><span className="label">Budget</span><span className="value budget-highlight">{getBudgetDisplay()}</span></div>
            <div><span className="label">Deadline</span><span className="value">{job.deadline ? new Date(job.deadline).toLocaleDateString() : 'Not specified'}</span></div>
            <div><span className="label">Currency</span><span className="value">{job.currency || 'USD'}</span></div>
            {job.classApproval && <div><span className="label">Class Approval</span><span className="value">{job.classApproval}</span></div>}
          </div>

          <div className="description-section">
            <h3>Description</h3>
            <p>{job.description || 'No description provided.'}</p>
          </div>

          <div className="requirements-section">
            <h3>Requirements</h3>
            <ul className="requirements-list">
              {job.classApproval && <li><i className="fas fa-check-circle"></i> Class {job.classApproval} Approval Required</li>}
              <li><i className="fas fa-check-circle"></i> Valid Insurance Certificate</li>
            </ul>
          </div>

          {hasSubmittedBid ? (
            <div className="contact-section" style={{ marginTop: '24px', textAlign: 'center' }}>
              <div className="contact-info">
                <h3>Your Quotation Status</h3>
                <p>Your quotation has been submitted. Status: <strong>{myBid?.status || 'Pending'}</strong></p>
                {canMessageOwner && (
                  <Button variant="primary" onClick={handleContactOwner} icon="fa-envelope" style={{ marginTop: '12px' }}>
                    Message Owner
                  </Button>
                )}
                {!canMessageOwner && myBid?.status === 'pending' && (
                  <p style={{ marginTop: '12px', fontSize: '12px', color: '#888' }}>
                    <i className="fas fa-info-circle"></i> The owner will contact you if they are interested.
                  </p>
                )}
              </div>
              {myBid && (
                <div className="my-bid-info" style={{ marginTop: '20px', padding: '16px', background: 'var(--bg-tertiary)', borderRadius: '10px' }}>
                  <h4>Your Quotation Details</h4>
                  <p>Amount: <strong>${myBid.amount?.toLocaleString()}</strong></p>
                  <p>Lead Time: <strong>{myBid.leadTime}</strong></p>
                  <p>Status: <span className={`bid-status ${myBid.status}`} style={{ color: myBid.status === 'accepted' ? '#10b981' : myBid.status === 'rejected' ? '#ef4444' : '#f59e0b' }}>{myBid.status}</span></p>
                  {myBid.status === 'accepted' && (
                    <Button variant="primary" onClick={() => navigate(`/service-provider/active-work`)} icon="fa-tasks" style={{ marginTop: '12px' }}>
                      Go to My Work
                    </Button>
                  )}
                </div>
              )}
            </div>
          ) : (
            // Submit Quotation Section
            isJobOpen && !isJobExpired ? (
              <div className="submit-bid-section" style={{ marginTop: '24px', textAlign: 'center', padding: '20px', background: 'rgba(24, 203, 150, 0.05)', borderRadius: '12px', border: '1px solid rgba(24, 203, 150, 0.2)' }}>
                <i className="fas fa-file-invoice" style={{ fontSize: '32px', color: 'var(--primary)', marginBottom: '12px' }}></i>
                <h3 style={{ marginBottom: '8px' }}>Interested in this job?</h3>
                <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '16px' }}>
                  Submit your detailed quotation with item-wise breakdown.
                </p>
                <Button 
                  variant="primary" 
                  size="large"
                  onClick={() => navigate(`/service-provider/submit-quotation/${id}`)} 
                  icon="fa-file-invoice"
                >
                  Submit Quotation
                </Button>
                <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '8px' }}>
                  10% platform fee applies on your quotation amount.
                </p>
              </div>
            ) : (
              <div className="job-closed-message" style={{ textAlign: 'center', padding: '30px', marginTop: '24px', background: 'rgba(0,0,0,0.3)', borderRadius: '12px' }}>
                <i className="fas fa-lock" style={{ fontSize: '48px', color: 'var(--text-tertiary)', marginBottom: '16px', opacity: 0.5 }}></i>
                <p style={{ color: 'var(--text-tertiary)' }}>
                  {isJobExpired ? 'This job has expired.' : 'This job is no longer accepting quotations.'}
                </p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorJobDetail;